/**
 * FlattradeCustomerDetails Component
 * Flattrade-specific customer details display with proper formatting for Flattrade API response
 * 
 * Trading Session Status Definitions (Source: Flattrade API Documentation):
 * - "Ok" = SUCCESS: API call successful, user session active
 * - "Not_Ok" = FAILED: API call failed, session expired or invalid
 * 
 * Data Source: /UserDetails endpoint via Flattrade Adapter Service
 * Example Response Structure:
 * {
 *   "stat": "Ok",
 *   "uid": "USER123",
 *   "uname": "John Doe", 
 *   "email": "user@example.com",
 *   "actid": "USER123",
 *   "brkname": "FLATTRADE",
 *   "request_time": "16:30:45 05-06-2023"
 * }
 * 
 * Reference: /backend/brokers/india/flattrade-adapter-service/docs/API Doc.md
 */

import React from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import {
  Person,
  AccountBox,
  Business,
  DateRange,
  Refresh,
  Info,
  Warning,
  Verified,
  Timeline,
  TrendingUp,
  Security,
} from "@mui/icons-material";
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const FlattradeCustomerDetails = ({ 
  account, 
  customerDetails, 
  lastUpdated, 
  onRefresh 
}) => {
  // Extract Flattrade customer data structure
  const customerData = customerDetails?.customer_details || customerDetails?.customer_data || customerDetails || {};
  
  // Map MUI chip color to DS badge variant (filled)
  const badgeVariant = (color) => {
    switch (color) {
      case "success": return "profit";
      case "error": return "loss";
      case "warning": return "warn";
      case "default":
      default: return "neutral";
    }
  };

  // Helper function to get session status based on Flattrade API response
  const getSessionStatus = (stat) => {
    switch (stat) {
      case "Ok": return { text: "Active", color: "success", icon: Verified };
      case "Not_Ok": return { text: "Expired", color: "error", icon: Warning };
      default: return { text: "Unknown", color: "default", icon: Warning };
    }
  };

  // Helper function to format Flattrade timestamp
  const formatFlattradeTime = (timeStr) => {
    if (!timeStr) return "N/A";
    // Flattrade format: "16:30:45 05-06-2023" or similar
    return timeStr;
  };

  // Helper function to get trading status
  const getTradingStatus = (customerData) => {
    // Since Flattrade doesn't provide exchange-wise status like ICICI,
    // we determine status based on session validity
    if (customerData.stat === "Ok") {
      return { text: "Active", color: "success" };
    } else {
      return { text: "Inactive", color: "default" };
    }
  };

  const sessionStatus = getSessionStatus(customerData.stat);
  const tradingStatus = getTradingStatus(customerData);

  // Personal information sections
  const personalInfo = [
    { label: "User Name", value: customerData.uname || customerData.username, icon: Person },
    { label: "Email", value: customerData.email, icon: AccountBox },
    { label: "Broker Name", value: customerData.brkname || "FLATTRADE", icon: Business },
    { label: "Request Time", value: formatFlattradeTime(customerData.request_time), icon: DateRange },
  ];

  // Account information
  const accountInfo = [
    { label: "User ID", value: customerData.uid || customerData.user_id },
    { label: "Account ID", value: customerData.actid || customerData.account_id },
    { label: "Session Status", value: sessionStatus.text },
    { label: "API Status", value: customerData.stat || "Unknown" },
  ];

  // Trading capabilities (Flattrade supports multiple segments)
  const tradingCapabilities = [
    { 
      label: "Equity Trading", 
      value: "Enabled", // Flattrade supports equity by default
      status: "enabled"
    },
    { 
      label: "F&O Trading", 
      value: "Enabled", // Flattrade supports F&O by default
      status: "enabled"
    },
    { 
      label: "Commodity Trading", 
      value: "Enabled", // Flattrade supports commodity by default
      status: "enabled"
    },
    { 
      label: "Currency Trading", 
      value: "Enabled", // Flattrade supports currency by default
      status: "enabled"
    },
  ];

  // API features available
  const apiFeatures = [
    { label: "Real-time Data", value: "WebSocket Available", icon: TrendingUp },
    { label: "Order Management", value: "Full API Support", icon: Business },
    { label: "Portfolio Tracking", value: "Holdings & Positions", icon: Timeline },
    { label: "Security", value: "Token-based Auth", icon: Security },
  ];

  return (
    <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
      <div className="p-6">
        <div className="space-y-6">
          {/* Header: Identity + Session */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] px-5 py-4 mb-2">
            <div className="flex items-center gap-4 min-w-0">
              <div className="bg-accent rounded-lg p-2 flex items-center justify-center shrink-0">
                <Person className="text-white" sx={{ fontSize: 24 }} />
              </div>
              <div className="min-w-0">
                <Typography variant="h6" className="text-fg-1 font-semibold break-words">
                  {customerData.uname || customerData.username || 'Flattrade User'}
                </Typography>
                <Typography variant="body2" className="text-fg-2 break-words">
                  User ID: {customerData.uid || customerData.user_id || 'N/A'}
                </Typography>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {lastUpdated && (
                <Typography variant="caption" className="text-fg-2">
                  Updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
              )}
              <Tooltip title="Refresh customer details">
                <IconButton
                  onClick={onRefresh}
                  size="small"
                  aria-label="Refresh customer details"
                  className="text-fg-2 hover:text-accent"
                  sx={{
                    '@media (pointer: coarse) and (max-width: 767px)': {
                      minWidth: 44,
                      minHeight: 44,
                    },
                  }}
                >
                  <Refresh sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <span className={`ds-badge sm ${badgeVariant(sessionStatus.color)}`}>
                <sessionStatus.icon sx={{ fontSize: 13 }} />
                {sessionStatus.text}
              </span>
            </div>
          </div>

          {/* Info Grid: Personal + Account Info */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card className="h-full bg-gradient-to-br from-white to-orange-50 dark:from-gray-800 dark:to-orange-900/20 border border-orange-200/50 dark:border-orange-700/50">
                <CardContent>
                  <Typography variant="subtitle1" className="font-semibold text-orange-700 dark:text-orange-300 mb-2 flex items-center gap-2">
                    <Info sx={{ fontSize: 18 }} /> Personal Info
                  </Typography>
                  <div className="space-y-2">
                    {personalInfo.map((item, idx) => item.value && (
                      <div key={idx} className="flex flex-wrap items-start gap-x-2 min-w-0">
                        <item.icon className="text-accent shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                        <Typography variant="body2" className="text-fg-1 font-medium shrink-0">
                          {item.label}:
                        </Typography>
                        <Typography variant="body2" className="text-fg-2 min-w-0 break-words">
                          {item.value}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className="h-full bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200/50 dark:border-emerald-700/50">
                <CardContent>
                  <Typography variant="subtitle1" className="font-semibold text-emerald-700 dark:text-emerald-300 mb-2 flex items-center gap-2">
                    <Business sx={{ fontSize: 18 }} /> Account Info
                  </Typography>
                  <dl className="space-y-1.5">
                    {accountInfo.map((item, idx) => item.value && (
                      <div key={idx} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0 py-0.5">
                        <dt className="text-fg-2 font-medium text-sm shrink-0">
                          {item.label}
                        </dt>
                        <dd className="text-fg-1 font-medium text-sm min-w-0 break-words">
                          {item.label === "Session Status" ? (
                            <span className={`ds-badge sm ${badgeVariant(sessionStatus.color)}`}>
                              {item.value}
                            </span>
                          ) : (
                            item.value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Trading Capabilities Section */}
          <Card className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
            <CardContent>
              <Typography variant="subtitle1" className="font-semibold text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-2">
                <TrendingUp sx={{ fontSize: 18 }} /> Trading Capabilities
              </Typography>
              <Grid container spacing={2}>
                {tradingCapabilities.map((item, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card className="rounded-lg border border-purple-200 dark:border-purple-600 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20">
                      <CardContent className="p-2 flex flex-col items-center">
                        <span className="ds-badge sm profit mb-1">
                          {item.value}
                        </span>
                        <Typography variant="caption" className="text-fg-2 font-medium text-center">
                          {item.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* API Features Section */}
          <Card className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
            <CardContent>
              <Typography variant="subtitle1" className="font-semibold text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-2">
                <Timeline sx={{ fontSize: 18 }} /> API Features
              </Typography>
              <Grid container spacing={2}>
                {apiFeatures.map((item, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card className="rounded-lg border border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20">
                      <CardContent className="p-2 flex flex-col items-start">
                        <div className="flex items-start gap-1 mb-1 min-w-0">
                          <item.icon className="text-accent shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                          <Typography variant="body2" className="text-fg-1 font-semibold min-w-0 break-words">
                            {item.label}
                          </Typography>
                        </div>
                        <span className="ds-badge sm info">
                          {item.value}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Trading Status Overview */}
          <Card className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
            <CardContent>
              <Typography variant="subtitle1" className="font-semibold text-indigo-700 dark:text-indigo-300 mb-2 flex items-center gap-2">
                <Security sx={{ fontSize: 18 }} /> Trading Status
              </Typography>
              <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg border border-indigo-200 dark:border-indigo-700">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg shrink-0 ${tradingStatus.color === 'success' ? 'bg-profit' : 'bg-bg-3'}`}>
                    <TrendingUp className={tradingStatus.color === 'success' ? 'text-white' : 'text-fg-2'} sx={{ fontSize: 20 }} />
                  </div>
                  <div className="min-w-0">
                    <Typography variant="body1" className="text-fg-1 font-semibold break-words">
                      Trading Status: {tradingStatus.text}
                    </Typography>
                    <Typography variant="caption" className="text-fg-2 break-words">
                      {customerData.stat === "Ok" ? "All markets accessible" : "Session requires refresh"}
                    </Typography>
                  </div>
                </div>
                <span className={`ds-badge ${badgeVariant(tradingStatus.color)}`}>
                  {tradingStatus.text}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* API Response Reference (Accordion) */}
          <Accordion className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] mt-2">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" className="font-semibold text-cyan-700 dark:text-cyan-300 flex items-center gap-2">
                <Info sx={{ fontSize: 16 }} /> Flattrade API Response Reference
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20">
                    <CardContent className="p-2 flex flex-col items-center">
                      <span className="ds-badge sm profit mb-1">Success</span>
                      <Typography variant="caption" className="text-fg-1 font-medium mb-1">stat: "Ok"</Typography>
                      <Typography variant="caption" className="text-fg-2 text-center">API call successful, session active</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card className="rounded-lg border border-red-200 dark:border-red-700 bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20">
                    <CardContent className="p-2 flex flex-col items-center">
                      <span className="ds-badge sm loss mb-1">Failed</span>
                      <Typography variant="caption" className="text-fg-1 font-medium mb-1">stat: "Not_Ok"</Typography>
                      <Typography variant="caption" className="text-fg-2 text-center">Session expired or invalid</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <div className="mt-3 p-2.5 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
                <Typography variant="caption" className="text-orange-700 dark:text-orange-300 block font-medium">
                  📘 Response format based on Flattrade API documentation
                </Typography>
                <Typography variant="caption" className="text-orange-600 dark:text-orange-400 block mt-0.5">
                  Supports real-time trading across all major Indian exchanges
                </Typography>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Data Source/Session Alert */}
          <div className="mt-2">
            <Card className="bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg shadow-md border-0 overflow-hidden">
              <CardContent className="p-3 flex items-center gap-3">
                <Info className="text-white" sx={{ fontSize: 20 }} />
                <div>
                  <Typography variant="body2" className="text-white font-semibold mb-1">
                    Data Source: Flattrade API (real-time)
                  </Typography>
                  <Typography variant="caption" className="text-orange-100 leading-relaxed">
                    Customer data retrieved directly from Flattrade API. Session status: {customerData.stat || 'Unknown'}.
                  </Typography>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlattradeCustomerDetails;
